import { Sparkles, X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LoyaltyProgram } from '@/types';

interface LoyaltyWelcomeModalProps {
  program: LoyaltyProgram;
  onSignUp: () => void;
  onDontShowAgain: () => void;
  onClose: () => void;
}

// Best booking-total percent discount across all tiers, for the "Save up to
// X%" teaser. Generic over the tiered taxonomy: a perk is a booking discount
// when its benefitKey maps to a booking_total target in the catalog.
function getMaxBookingDiscountPct(program: LoyaltyProgram): number | null {
  const catalog = program.config.targetCatalog ?? {};
  let max: number | null = null;
  for (const tier of program.config.tiers) {
    for (const perk of tier.perks) {
      if (perk.type !== 'discount' || perk.value.mode !== 'percent') continue;
      const targets = catalog[perk.benefitKey] ?? [];
      if (!targets.some((target) => target.kind === 'booking_total')) continue;
      const pct = perk.value.amount ?? 0;
      if (pct > 0 && (max === null || pct > max)) max = pct;
    }
  }
  return max;
}

export default function LoyaltyWelcomeModal({
  program,
  onSignUp,
  onDontShowAgain,
  onClose,
}: LoyaltyWelcomeModalProps) {
  const { t } = useTranslation('portal');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const discountPct = getMaxBookingDiscountPct(program);
  const hasMultipleTiers = program.config.tiers.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-4 sm:px-6 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loyalty-welcome-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t('Close')}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-zinc-500 hover:bg-zinc-100"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-7 pb-5">
          <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <h2
            id="loyalty-welcome-title"
            className="mt-4 text-[22px] font-semibold text-zinc-900 tracking-tight"
          >
            {t('Join our loyalty program')}
          </h2>
          <p className="mt-2 text-[14px] text-zinc-600 leading-relaxed">
            {program.description ||
              t('Members get exclusive perks on every stay, starting with this one.')}
          </p>

          {discountPct && (
            <ul className="mt-5 space-y-2.5 text-[14px] text-zinc-700">
              <li className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E3A5F] flex-shrink-0" />
                <span>{t('Save up to {{pct}}% on every stay', { pct: discountPct })}</span>
              </li>
              {hasMultipleTiers && (
                <li className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E3A5F] flex-shrink-0" />
                  <span>{t('Free nights, add-on credit, and more across our tiers')}</span>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="px-6 pb-6 space-y-2">
          <button
            type="button"
            onClick={onSignUp}
            className="w-full h-12 rounded-full text-white text-[15px] font-medium transition"
            style={{ backgroundColor: 'var(--besty-blue)' }}
          >
            {t('Sign up')}
          </button>
          <button
            type="button"
            onClick={onDontShowAgain}
            className="w-full h-11 rounded-full text-[14px] font-medium text-zinc-500 hover:bg-zinc-50"
          >
            {t("Don't show again")}
          </button>
        </div>
      </div>
    </div>
  );
}
